import { useCallback, useEffect, useRef } from 'react';

const PollMs = 60_000;
const MinGapMs = 10_000;

export interface FreshnessCheckOptions {
  /** Also check every 60 s while this is true and the tab is visible, for example while a job runs. */
  pollWhile?: boolean;
}

/**
 * Runs `check`, a cheap read of state that pushes may have missed: when the tab becomes visible, when the network
 * comes back, and on the poll `pollWhile` asks for. Checks start at least 10 s apart; a trigger inside that gap runs
 * one check when the gap ends.
 */
export function useFreshnessCheck(check: () => unknown, { pollWhile = false }: FreshnessCheckOptions = {}): void {
  const checkRef = useRef(check);
  useEffect(() => {
    checkRef.current = check;
  });

  const lastCheckAtRef = useRef(Number.NEGATIVE_INFINITY);
  const pendingRef = useRef<number | undefined>(undefined);

  const requestCheck = useCallback(() => {
    if (pendingRef.current !== undefined) return;
    const run = () => {
      pendingRef.current = undefined;
      lastCheckAtRef.current = Date.now();
      checkRef.current();
    };
    const wait = lastCheckAtRef.current + MinGapMs - Date.now();
    if (wait <= 0) run();
    else pendingRef.current = window.setTimeout(run, wait);
  }, []);

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') requestCheck();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('online', requestCheck);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('online', requestCheck);
      window.clearTimeout(pendingRef.current);
      pendingRef.current = undefined;
    };
  }, [requestCheck]);

  useEffect(() => {
    if (!pollWhile) return;
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') requestCheck();
    }, PollMs);
    return () => window.clearInterval(timer);
  }, [pollWhile, requestCheck]);
}
